/**
 * AttemptRecorder as a hook.
 *
 * The recorder instance deliberately outlives re-renders and stage changes: a
 * stage transition (placement → paper_check → recording) must never tear down
 * an active MediaRecorder or drop the queued chunks.
 *
 * What rebuilds it is any change to `attemptId`, `source`, `stream`, `clock`,
 * `deviceToken` or `videoBitsPerSecond`. The last three matter: they look
 * inert, but an inline object or a fresh `ServerClock` passed on every render
 * would rebuild the recorder on every render and lose the in-flight chunk each
 * time. Callers must hold them stable — `onStreamLost`/`onStall` are refs
 * precisely so they need not be.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { RecordingSource } from "@exam/shared";
import type { ServerClock } from "@/lib/exam/serverClock";
import { AttemptRecorder, type RecorderStatus } from "./recorder";
import { purgeChunksOlderThan } from "./chunkStore";

/** Chunks from attempts this old are unreachable — the API refuses them. */
const STALE_CHUNK_AGE_MS = 24 * 60 * 60 * 1000;

export interface UseRecorderOptions {
  attemptId: string | null;
  source: RecordingSource;
  stream: MediaStream | null;
  clock: ServerClock;
  deviceToken?: string;
  /** Override the encode bitrate — screen capture needs more than a face. */
  videoBitsPerSecond?: number;
  /** Start/stop recording. Toggling this does NOT rebuild the recorder. */
  recording: boolean;
  /**
   * Called when the recorder loses its camera (the video track ended). The owner
   * should re-acquire the camera; a fresh stream rebuilds the recorder and
   * recording resumes. Belt-and-suspenders alongside useCamera's own track-end
   * recovery.
   */
  onStreamLost?: () => void;
  /**
   * The recorder produced nothing for long enough to count as broken. The
   * runner logs a proctor event so the reviewer is told at the moment it
   * happened, rather than left to infer it from a gap in the video.
   */
  onStall?: (info: {
    silentForMs: number;
    willRestart: boolean;
    reason: "encoder" | "storage";
  }) => void;
}

export interface RecorderHandle {
  status: RecorderStatus;
  /** Drains the queue and stops the uploader — call after submit. */
  finishUploads: () => Promise<void>;
}

const IDLE_STATUS: RecorderStatus = {
  recording: false,
  chunksProduced: 0,
  upload: {
    pending: 0,
    pendingBytes: 0,
    uploading: false,
    failures: 0,
    uploaded: 0,
    refused: 0,
    lastErrorMessage: null,
    lastUploadAtMs: null,
    wedgeRecoveries: 0,
  },
  error: null,
  stalled: false,
  lastChunkAtMs: null,
};

export function useRecorder(options: UseRecorderOptions): RecorderHandle {
  const {
    attemptId,
    source,
    stream,
    clock,
    deviceToken,
    videoBitsPerSecond,
    recording,
  } = options;

  // Held in a ref so the recorder-build effect does not depend on (and rebuild
  // on) a caller passing a fresh inline `onStreamLost` each render.
  const onStreamLostRef = useRef(options.onStreamLost);
  onStreamLostRef.current = options.onStreamLost;
  const onStallRef = useRef(options.onStall);
  onStallRef.current = options.onStall;

  // The instance lives in state, not a ref, on purpose: the start/stop effect
  // below has to re-run when it is replaced. With a ref, a recorder built AFTER
  // `recording` had already turned true was never started — the start effect's
  // dependencies had not changed, so it never looked at the new instance. That
  // is the normal order on a phone, where the camera stream arrives after the
  // pair code has been claimed, and it left the side camera silently idle.
  const [recorder, setRecorder] = useState<AttemptRecorder | null>(null);
  const [status, setStatus] = useState<RecorderStatus>(IDLE_STATUS);

  // One-time housekeeping so an abandoned attempt's chunks do not accumulate.
  useEffect(() => {
    void purgeChunksOlderThan(STALE_CHUNK_AGE_MS);
  }, []);

  useEffect(() => {
    if (!attemptId || !stream) return;

    const instance = new AttemptRecorder({
      attemptId,
      source,
      stream,
      clock,
      deviceToken,
      videoBitsPerSecond,
      onStatus: setStatus,
      onStreamLost: () => onStreamLostRef.current?.(),
      onStall: (info) => onStallRef.current?.(info),
    });
    setRecorder(instance);

    return () => {
      setRecorder((current) => (current === instance ? null : current));
      void instance.dispose();
    };
  }, [attemptId, source, stream, clock, deviceToken, videoBitsPerSecond]);

  // Start/stop is a separate effect so flipping `recording` does not rebuild
  // the recorder (and thus does not lose the in-flight chunk).
  useEffect(() => {
    if (!recorder) return;

    if (recording && !recorder.isRecording) {
      recorder.start();
    } else if (!recording && recorder.isRecording) {
      void recorder.stop();
    }
  }, [recorder, recording, status.recording]);

  const finishUploads = useCallback(async () => {
    if (!recorder) return;
    await recorder.stop();
    await recorder.finishUploads();
  }, [recorder]);

  return { status, finishUploads };
}
